import React from "react";
import {
  Button,
  Checkbox,
  Input,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  SaveOutlined,
  SearchOutlined,
  StopOutlined,
} from "@ant-design/icons";
import useBulkQualifications, {
  StudentQualificationRow,
} from "../../hooks/qualifications/useBulkQualifications";
import { QualificationStatusBadge } from "../../../../shared/components/ui/AcademyStatusBadge";

const { Text } = Typography;

interface Props {
  programId: number;
}

export default function TabBulkQualifications({ programId }: Props) {
  const {
    loading,
    saving,
    modules,
    topics,
    techniques,
    selectedModuleId,
    selectedTopicId,
    selectedTechniqueId,
    rows,
    selectedUserIds,
    readyToSaveCount,
    setSelectedModuleId,
    setSelectedTopicId,
    setSelectedTechniqueId,
    toggleRow,
    setSelectAll,
    saveRow,
    applyToSelected,
    saveAll,
  } = useBulkQualifications(programId);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [tablePage, setTablePage] = React.useState(1);

  React.useEffect(() => {
    setSearchQuery("");
    setTablePage(1);
  }, [selectedTechniqueId]);

  React.useEffect(() => {
    setTablePage(1);
  }, [searchQuery]);

  const normalizeSearchText = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const filteredRows = React.useMemo(() => {
    const query = normalizeSearchText(searchQuery);
    if (!query) return rows;
    return rows.filter((row) => {
      const name = normalizeSearchText(row.userName);
      const code = normalizeSearchText(row.studentCode ?? "");
      return name.includes(query) || code.includes(query);
    });
  }, [rows, searchQuery]);

  const filteredUserIds = React.useMemo(
    () => filteredRows.map((row) => row.userId),
    [filteredRows]
  );

  const isAllFilteredSelected =
    filteredRows.length > 0 &&
    filteredRows.every((row) => selectedUserIds.includes(row.userId));

  const isFilteredIndeterminate =
    filteredRows.some((row) => selectedUserIds.includes(row.userId)) &&
    !isAllFilteredSelected;

  const hasSelection = selectedUserIds.length > 0;

  const columns = [
    {
      title: (
        <Checkbox
          checked={isAllFilteredSelected}
          indeterminate={isFilteredIndeterminate}
          disabled={filteredRows.length === 0}
          onChange={(e) => setSelectAll(e.target.checked, filteredUserIds)}
        >
          <span className="text-xs font-normal">Todos</span>
        </Checkbox>
      ),
      key: "select",
      width: 90,
      fixed: "left" as const,
      render: (_: unknown, row: StudentQualificationRow) => (
        <Checkbox
          checked={selectedUserIds.includes(row.userId)}
          onChange={(e) => toggleRow(row.userId, e.target.checked)}
        />
      ),
    },
    {
      title: "Código",
      dataIndex: "studentCode",
      width: 110,
      render: (val: string) => (
        <Text className="text-xs text-gray-500">{val ?? "—"}</Text>
      ),
    },
    {
      title: "Estudiante",
      dataIndex: "userName",
      width: 200,
      render: (val: string, row: StudentQualificationRow) => (
        <Text strong={selectedUserIds.includes(row.userId)}>{val}</Text>
      ),
    },
    {
      title: "Estado",
      dataIndex: "qualification_check",
      width: 130,
      render: (_: unknown, row: StudentQualificationRow) => {
        if (row.saving) {
          return <LoadingOutlined className="text-blue-500" spin />;
        }
        return row.qualification_check ? (
          <QualificationStatusBadge status={row.qualification_check} variant="compact" />
        ) : (
          <Tag color="default">Sin asignar</Tag>
        );
      },
    },
    {
      title: "Acción",
      key: "status_actions",
      width: 155,
      render: (_: unknown, row: StudentQualificationRow) => (
        <div className="flex gap-1">
          <Tooltip title="Aprobar">
            <Button
              size="small"
              type={row.qualification_check === "approved" ? "primary" : "default"}
              icon={<CheckCircleOutlined />}
              loading={row.saving}
              className={
                row.qualification_check !== "approved"
                  ? "text-green-600 border-green-400"
                  : ""
              }
              onClick={() => saveRow(row.userId, "approved")}
            />
          </Tooltip>
          <Tooltip title="Reprobar">
            <Button
              size="small"
              danger={row.qualification_check === "disapproved"}
              type={row.qualification_check === "disapproved" ? "primary" : "default"}
              icon={<CloseCircleOutlined />}
              loading={row.saving}
              onClick={() => saveRow(row.userId, "disapproved")}
            />
          </Tooltip>
          <Tooltip title="Quitar estado">
            <Button
              size="small"
              icon={<StopOutlined />}
              loading={row.saving}
              disabled={!row.qualification_check}
              onClick={() => saveRow(row.userId, null)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Selectores en cascada */}
      <div className="flex flex-wrap items-end gap-4 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
            Módulo
          </label>
          <Select
            style={{ width: 220 }}
            placeholder="Selecciona el módulo"
            value={selectedModuleId ?? undefined}
            onChange={(val) => setSelectedModuleId(val ?? null)}
            options={modules.map((m) => ({ value: m.id, label: m.name }))}
            showSearch
            optionFilterProp="label"
            allowClear
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
            Tema
          </label>
          <Select
            style={{ width: 220 }}
            placeholder="Selecciona el tema"
            value={selectedTopicId ?? undefined}
            disabled={!selectedModuleId}
            onChange={(val) => setSelectedTopicId(val ?? null)}
            options={topics.map((t) => ({ value: t.id, label: t.name }))}
            showSearch
            optionFilterProp="label"
            allowClear
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
            Técnica
          </label>
          <Select
            style={{ width: 220 }}
            placeholder="Selecciona la técnica"
            value={selectedTechniqueId ?? undefined}
            disabled={!selectedTopicId}
            onChange={(val) => setSelectedTechniqueId(val ?? null)}
            options={techniques.map((t) => ({ value: t.id, label: t.name }))}
            showSearch
            optionFilterProp="label"
            allowClear
          />
        </div>
      </div>

      {/* Buscador */}
      {selectedTechniqueId && rows.length > 0 && (
        <div className="mb-4">
          <Input
            allowClear
            size="large"
            prefix={<SearchOutlined className="text-gray-400" />}
            placeholder="Buscar por nombre o código de estudiante"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ maxWidth: 420 }}
          />
          {searchQuery.trim() && (
            <Text className="block mt-2 text-sm text-gray-500">
              Mostrando {filteredRows.length} de {rows.length} estudiantes
            </Text>
          )}
        </div>
      )}

      {/* Barra de acciones masivas */}
      {selectedTechniqueId && rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 p-3 bg-white rounded-lg border border-gray-200">
          <div className="flex flex-wrap gap-2 items-center">
            <Text className="text-sm text-gray-600 mr-2">
              <strong>{selectedUserIds.length}</strong> de {rows.length} seleccionados
            </Text>
            <Button
              size="small"
              onClick={() => setSelectAll(true, filteredUserIds)}
              disabled={filteredRows.length === 0}
            >
              Seleccionar todos
            </Button>
            <Button
              size="small"
              onClick={() => setSelectAll(false, filteredUserIds)}
              disabled={!hasSelection}
            >
              Deseleccionar todos
            </Button>
            <span className="text-gray-300 mx-1">|</span>
            <Button
              size="small"
              icon={<CheckCircleOutlined />}
              className="text-green-600 border-green-400"
              disabled={!hasSelection}
              onClick={() => applyToSelected("approved")}
            >
              Aprobar seleccionados
            </Button>
            <Button
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              disabled={!hasSelection}
              onClick={() => applyToSelected("disapproved")}
            >
              Reprobar seleccionados
            </Button>
            <Button
              size="small"
              icon={<StopOutlined />}
              disabled={!hasSelection}
              onClick={() => applyToSelected(null)}
            >
              Quitar estado
            </Button>
          </div>

          <Button
            type="primary"
            icon={<SaveOutlined />}
            size="large"
            shape="round"
            loading={saving}
            onClick={saveAll}
            disabled={readyToSaveCount === 0}
          >
            Guardar seleccionados ({readyToSaveCount})
          </Button>
        </div>
      )}

      {/* Estado vacío */}
      {!selectedTechniqueId && (
        <div className="text-center text-gray-400 py-20">
          <div className="text-5xl mb-4">📋</div>
          <Text className="text-base text-gray-400">
            Selecciona un módulo, tema y técnica para ver el listado de estudiantes
          </Text>
        </div>
      )}

      {/* Tabla */}
      {selectedTechniqueId && (
        <Spin spinning={loading} tip="Cargando estudiantes...">
          <Table<StudentQualificationRow>
            key={`${selectedTechniqueId}-${searchQuery.trim().toLowerCase()}`}
            dataSource={filteredRows}
            columns={columns}
            rowKey={(row) => `student-${row.userId}`}
            pagination={{
              current: tablePage,
              total: filteredRows.length,
              pageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ["25", "50", "100"],
              onChange: (page) => setTablePage(page),
              hideOnSinglePage: true,
            }}
            size="middle"
            scroll={{ x: 760 }}
            rowClassName={(row) =>
              selectedUserIds.includes(row.userId) ? "bg-blue-50" : ""
            }
            locale={{
              emptyText: searchQuery.trim()
                ? "No se encontraron estudiantes con ese criterio"
                : "No hay estudiantes en este programa",
            }}
          />
        </Spin>
      )}
    </div>
  );
}
